import React, { useEffect, useState } from 'react';
import { Link2 } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { errMsg } from '@/services/staffAttendanceApi';
import { tallyApi } from '@/services/ewayBillApi';

export const slug = (s) => String(s || '').toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '');
const Chip = ({ r, missingLabel = 'missing' }) => {
  const [l, c] = r.found === null ? ['not checked', 'bg-gray-100 text-gray-600']
    : r.found ? [r.mapped ? 'mapped' : r.by_gstin ? 'matched by GSTIN' : 'in Tally', 'bg-emerald-100 text-emerald-800']
      : r.create ? ['WMS creates it in Tally', 'bg-sky-100 text-sky-800'] : [missingLabel, 'bg-rose-100 text-rose-700'];
  return <span className={`rounded-full px-2 text-[11px] font-semibold ${c}`}>{l}</span>;
};

export const NameRow = ({ kind, r, onMapped, allowChange, startClosed = false, missingLabel, label }) => {
  const id = `tally-map-${kind}-${slug(r.wms)}`;
  const [v, setV] = useState('');
  const [opts, setOpts] = useState(null);
  const [edit, setEdit] = useState(false);
  const open = (r.found === false && !r.create && !startClosed) || edit;
  useEffect(() => {
    if (!open) return undefined;
    const t = setTimeout(() => tallyApi.masters({ kind, q: v }).then(setOpts).catch(() => {}), 250);
    return () => clearTimeout(t);
  }, [kind, v, open]);
  const save = () => tallyApi.mapName({ kind, wms: r.wms, tally: v })
    .then(() => { toast.success(`${r.wms} → ${v}`); setEdit(false); setV(''); onMapped(); }).catch((e) => toast.error(errMsg(e, 'Map failed')));
  return (
    <div className="flex flex-wrap items-center gap-2 py-1.5 text-sm" data-testid={id}>
      <span className="min-w-[3.5rem] whitespace-nowrap text-[11px] uppercase text-gray-500">{label || (kind === 'ledger' ? 'Party' : 'Item')}</span>
      <span className="font-medium">{r.wms}</span>
      {r.tally !== r.wms && <span className="text-gray-500">→ {r.tally}</span>}
      <Chip r={r} missingLabel={missingLabel} />
      {!open && (allowChange || r.create) && (
        <Button size="sm" variant="ghost" className="ml-auto h-7 text-xs" onClick={() => setEdit(true)} data-testid={`${id}-change`}>
          {r.create ? 'Use an existing Tally name' : r.found === false ? 'Map' : 'Change'}</Button>
      )}
      {open && (
        <div className="ml-auto flex items-center gap-1">
          {v.trim() && opts && !opts.length && <span className="text-xs text-amber-700" data-testid={`${id}-nomatch`}>No match in Tally</span>}
          <Input value={v} onChange={(e) => setV(e.target.value)} list={`${id}-list`} placeholder={`Pick Tally ${kind}`} className="h-8 w-56" data-testid={`${id}-input`} />
          <datalist id={`${id}-list`}>{(opts || []).map((o) => <option key={o} value={o} />)}</datalist>
          <Button size="sm" variant="outline" onClick={save} disabled={!v.trim()} data-testid={`${id}-save`}><Link2 className="w-4 h-4 mr-1" />Map</Button>
          {edit && <Button size="sm" variant="ghost" onClick={() => { setEdit(false); setV(''); }} data-testid={`${id}-cancel`}>Cancel</Button>}
        </div>
      )}
    </div>
  );
};

const Accounts = ({ a }) => (
  <div className="flex flex-wrap items-center gap-2 py-1.5 text-sm" data-testid="tally-accounts">
    <span className="w-14 text-[11px] uppercase text-gray-500">Ledgers</span>
    {Object.entries(a).map(([k, x]) => (
      <span key={k} className="flex items-center gap-1" data-testid={`tally-account-${k}`}>
        <span className="text-gray-500">{x.role}:</span><span className="font-medium">{x.tally}</span><Chip r={{ ...x, mapped: false }} />
      </span>
    ))}
  </div>
);

const plural = (n, one, many) => `${n} ${n === 1 ? one : many}`;
export const CreatePlan = ({ c, testId = 'tally-create-summary' }) => {
  if (!c) return null;
  const parts = [c.ledgers?.length && plural(c.ledgers.length, 'party ledger', 'party ledgers'), c.items?.length && plural(c.items.length, 'stock item', 'stock items')].filter(Boolean);
  const item = c.items?.[0];
  return (
    <p className="rounded-md border border-sky-200 bg-sky-50 px-2 py-1 text-xs text-sky-900" data-testid={testId}>
      WMS creates <b>{parts.join(' and ')}</b> in Tally first, then the voucher.
      {item && <> Stock items go under <b>{item.parent || 'Primary'}</b>, unit <b>{item.unit}</b>, with HSN and GST (5% if TN WSP ≤ ₹2,500, else 18%).</>}
      {c.ledgers?.[0] && <> Ledger under <b>{c.ledgers[0].parent}</b> with GSTIN, address, state and PIN from {c.ledgers[0].gstin ? 'Customer Master' : 'the document'}.</>}
    </p>
  );
};

/** Tally route review: which Tally ledger / stock items the voucher will use; missing ones can be mapped to an existing Tally name. */
export const TallyNames = ({ tally: t, onMapped, allowChange = false }) => {
  const [all, setAll] = useState(false);
  const many = t.items.length > 8;
  const shown = many && !all ? t.items.filter((i) => i.found === false) : t.items;
  return (
    <div className="rounded-lg border border-teal-200 bg-teal-50/40 p-3" data-testid="tally-names">
      <div className="text-sm font-semibold text-teal-900">Tally voucher: {t.vch_type} <span className="font-normal text-gray-600">· company {t.company || '— not set —'}</span></div>
      {!t.masters_synced_at && <p className="text-xs text-amber-700" data-testid="tally-names-unchecked">Tally masters not read yet — names can't be checked until the Tally link runs.</p>}
      <CreatePlan c={t.create} />
      <div className="divide-y">
        <NameRow kind="ledger" r={t.party} onMapped={onMapped} allowChange={allowChange} />
        {t.accounts && <Accounts a={t.accounts} />}
        {shown.map((i) => <NameRow key={i.wms} kind="item" r={i} onMapped={onMapped} allowChange={allowChange} />)}
      </div>
      {many && (
        <Button size="sm" variant="ghost" className="mt-1 h-7 text-xs text-teal-900" onClick={() => setAll(!all)} data-testid="tally-names-toggle">
          {all ? 'Show only missing / new stock items' : `Show all ${t.items.length} stock items (${t.items.length - shown.length} hidden)`}
        </Button>
      )}
    </div>
  );
};

export default TallyNames;
